import React, { Component } from 'react'
import { Descriptions, Form, FormInstance, Select, Tag } from 'antd'
import { BreedConsumer, BreedContextData } from '../../../../context/BreedContext'
import { SpecieConsumer, SpecieContextData } from '../../../../context/SpecieContext'
import { ShelterConsumer, ShelterContextData } from 'context/ShelterContext'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import { PartnerKeyType } from 'interfaces/IIntegration'
import { sbListAnimalSpecies } from 'services/integration'
import { ExternalOption } from '../ShelterBuddyExternalField'
import ShelterBuddySearchField from '../ShelterBuddySearchField'
import MappingStepper, { Step } from '../MappingStepper'
import { DIRECTION_LABEL, Flow, Partner } from '../shared/types'
import { resolveFlow } from '../shared/resolvers'
import { ActiveField, IncomingStep, IntegrationField, ManualExternalInputs, PartnerField, ScopeField } from '../shared/fields'

/**
 * Searches Shelter Buddy breeds by name. The species endpoint returns one row per
 * species (breed × class × type), so we pull the `Breed` off each row and dedupe.
 */
const searchBreeds = async (integrationUuid: string, term: string): Promise<ExternalOption[]> => {
  const response = await sbListAnimalSpecies({ integration_uuid: integrationUuid, filters: { breed_name: term } })

  if (response.data.status !== 200) {
    const message =
      (response.data as any)?.message ||
      `Failed to search breeds (status ${response.data?.status ?? 'unknown'})`
    console.error(response.data)
    throw new Error(message)
  }

  const seen = new Set<number>()
  const options: ExternalOption[] = []
  for (const row of response.data.data ?? []) {
    const breed = row.Breed
    if (!breed || seen.has(breed.Id)) continue
    seen.add(breed.Id)
    options.push({
      label: `${breed.Name} [${breed.Id}]`,
      name: breed.Name,
      value: breed.Id.toString(),
    })
  }
  return options
}

type MappingBreedFormProps = {
  form: FormInstance
  partnerList: Partner[] | null
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingBreedFormInnerProps = MappingBreedFormProps & {
  breedContext: BreedContextData
  specieContext: SpecieContextData
  shelterContext: ShelterContextData
}

type MappingBreedFormInnerState = {
  flow: Flow
}

class MappingBreedFormInner extends Component<MappingBreedFormInnerProps, MappingBreedFormInnerState> {
  constructor(props: MappingBreedFormInnerProps) {
    super(props)
    this.state = {
      flow: resolveFlow(props.form, 'breed_uuid'),
    }
  }

  componentDidMount() {
    const { form, specieContext, breedContext, shelterContext } = this.props

    // Initial values may settle after the Form mounts — re-resolve the flow.
    const flow = resolveFlow(form, 'breed_uuid')
    if (flow !== this.state.flow) {
      this.setState({ flow })
    }

    if (!specieContext.fetching && !specieContext.species?.length) {
      specieContext.requestSpecieList({
        options: {
          limit: 9999,
          sort: ['name']
        }
      })
    }

    // Load the breeds scoped to the mapping's species (so an edit shows its breed).
    const specieUuid = form?.getFieldValue('specie_uuid')
    const breedPayload: any = {
      options: { limit: 9999, sort: ['name'] },
      ...(specieUuid ? { search: { specie: { has: { uuid: specieUuid } } } } : {}),
    }

    if (
      (!breedContext.fetching && !breedContext.breeds?.length) ||
      (breedContext.lastListPayload && (breedContext.lastListPayload.search as any)?.specie?.has?.uuid !== specieUuid)
    ) {
      breedContext.requestBreedList(breedPayload)
    }

    if (!shelterContext.fetching && !shelterContext.shelters?.length) {
      shelterContext.requestShelterList({
        options: {
          limit: 9999,
          sort: ['name']
        },
        search: {
          _fields: ["id", "uuid", "name"],
          active: true,
          deleted: false,
          organization_integrations: {
            with: {
              _fields: ["id", "uuid", "description", "partner_key", "main", "shelter_id"],
              active: true,
              deleted: false,
            }
          }
        }
      })
    }
  }

  /** Reload the breed options for the chosen species and clear the stale breed. */
  handleChangeSpecie = (specieUuid: string) => {
    const { form, breedContext } = this.props

    form.setFieldsValue({ breed_uuid: undefined })

    breedContext.requestBreedList({
      search: {
        specie: { has: { uuid: specieUuid } }
      },
      options: { limit: 9999, sort: ['name'] }
    })
  }

  /** Resolve a shelter name for display, from the loaded shelter list. */
  shelterName = (uuid?: string) =>
    this.props.shelterContext.shelters?.find(s => s.uuid === uuid)?.name

  /** Resolve a partner display name. */
  partnerName = (key?: string) =>
    this.props.partnerList?.find(p => p.key === key)?.name || key

  /** Resolve a species name for display, from the loaded species list. */
  specieName = (uuid?: string) =>
    this.props.specieContext.species?.find(s => s.uuid === uuid)?.name

  /** Resolve a breed name for display, from the loaded breed list. */
  breedName = (uuid?: string) =>
    this.props.breedContext.breeds?.find(b => b.uuid === uuid)?.name

  // ----- Field groups -----

  renderSpeciesField = () => {
    const { specieContext } = this.props
    const speciesOptions = specieContext.species?.map(specie => ({
      label: specie.name,
      value: specie.uuid,
    }))

    return (
      <Form.Item
        label="Species"
        name="specie_uuid"
        rules={[{ required: true, message: 'Missing a species' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          loading={specieContext.fetching}
          placeholder="Select a species"
          options={speciesOptions}
          onChange={(value: string) => this.handleChangeSpecie(value)}
        />
      </Form.Item>
    )
  }

  renderBreedField = () => (
    <Form.Item
      noStyle
      shouldUpdate={(prev, cur) => prev.specie_uuid !== cur.specie_uuid}
    >
      {({ getFieldValue }) => {
        const { breedContext } = this.props
        const specieUuid = getFieldValue('specie_uuid')
        const breedsOptions = breedContext.breeds?.map(breed => ({
          label: breed.name,
          value: breed.uuid as string,
        }))

        return (
          <Form.Item
            label="Breed"
            name="breed_uuid"
            rules={[{ required: true, message: 'Missing a breed' }]}
          >
            <Select
              showSearch
              filterOption={DEFAULT_SELECT_FILTER_OPTION}
              loading={breedContext.fetching}
              disabled={!specieUuid}
              placeholder="Select a breed"
              options={breedsOptions}
            />
          </Form.Item>
        )
      }}
    </Form.Item>
  )

  /** External target — Shelter Buddy breed search, or name + id inputs. */
  renderExternalTarget = () => {
    const { form } = this.props

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.shelter_uuid !== cur.shelter_uuid ||
          prev.partner_key !== cur.partner_key ||
          prev.integration_uuid !== cur.integration_uuid
        }
      >
        {({ getFieldValue }) => {
          const shelterUuid: string | undefined = getFieldValue("shelter_uuid")
          const partnerKey: PartnerKeyType | undefined = getFieldValue("partner_key")
          const integrationUuid: string | undefined = getFieldValue("integration_uuid")

          const canLoadExternal =
            !!shelterUuid && !!integrationUuid && partnerKey === "SHELTER_BUDDY"

          if (canLoadExternal) {
            return (
              <ShelterBuddySearchField
                form={form}
                integrationUuid={integrationUuid!}
                searchExternalList={searchBreeds}
                label="External breed"
                placeholder="Search a breed by name"
              />
            )
          }

          return <ManualExternalInputs />
        }}
      </Form.Item>
    )
  }

  /** Inbound only: read-only display of the incoming external value. */
  renderIncoming = () => (
    <IncomingStep
      form={this.props.form}
      partnerList={this.props.partnerList}
      externalRowLabel="External Breed"
    />
  )

  // ----- Review -----

  renderReview = () => {
    const { form } = this.props

    const partnerKey = form.getFieldValue('partner_key')
    const shelterUuid = form.getFieldValue('shelter_uuid')
    const specieUuid = form.getFieldValue('specie_uuid')
    const breedUuid = form.getFieldValue('breed_uuid')
    const name = form.getFieldValue('name')
    const externalId = form.getFieldValue('external_id')

    const ourSide = this.breedName(breedUuid) || '—'
    const externalSide = [name, externalId].filter(Boolean).join(' · ') || '—'
    const mappingSummary = `${ourSide} ↔ ${externalSide}`

    return (
      <>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Direction">
            {DIRECTION_LABEL.BOTH}
          </Descriptions.Item>
          <Descriptions.Item label="Partner">{this.partnerName(partnerKey) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Species">{this.specieName(specieUuid) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mapping">{mappingSummary}</Descriptions.Item>
          <Descriptions.Item label="Applies to">
            {shelterUuid ? (this.shelterName(shelterUuid) || shelterUuid) : <Tag>GLOBAL</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <ActiveField />
      </>
    )
  }

  // ----- Step definitions -----

  steps = (): Step[] => {
    const { form, shelterContext } = this.props

    const reviewStep: Step = {
      key: 'review',
      title: 'Review & Save',
      fields: [],
      content: this.renderReview,
    }

    if (this.state.flow === 'inbound') {
      return [
        {
          key: 'incoming',
          title: 'Incoming',
          fields: [],
          content: this.renderIncoming,
        },
        {
          key: 'our-side',
          title: 'Our side',
          fields: ['specie_uuid', 'breed_uuid', 'shelter_uuid'],
          content: () => (
            <>
              {this.renderSpeciesField()}
              {this.renderBreedField()}
              <ScopeField form={form} shelterContext={shelterContext} />
            </>
          ),
        },
        reviewStep,
      ]
    }

    return [
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['specie_uuid', 'breed_uuid', 'partner_key', 'shelter_uuid'],
        content: () => (
          <>
            {this.renderSpeciesField()}
            {this.renderBreedField()}
            <PartnerField partnerList={this.props.partnerList} />
            <ScopeField form={form} shelterContext={shelterContext} />
          </>
        ),
      },
      {
        key: 'external',
        title: 'External target',
        fields: ['name', 'external_id'],
        content: () => (
          <>
            <IntegrationField shelterContext={shelterContext} />
            {this.renderExternalTarget()}
          </>
        ),
      },
      reviewStep,
    ]
  }

  render() {
    return <MappingStepper form={this.props.form} steps={this.steps()} loading={this.props.loading} />
  }
}

export default class MappingBreedForm extends Component<MappingBreedFormProps> {
  render() {
    return (
      <SpecieConsumer>
        {(specieContext) =>
          <BreedConsumer>
            {(breedContext) =>
              <ShelterConsumer>
                {(shelterContext) =>
                  <MappingBreedFormInner
                    form={this.props.form}
                    partnerList={this.props.partnerList}
                    loading={this.props.loading}
                    specieContext={specieContext}
                    breedContext={breedContext}
                    shelterContext={shelterContext}
                  />
                }
              </ShelterConsumer>
            }
          </BreedConsumer>
        }
      </SpecieConsumer>
    )
  }
}
